/* eslint-disable @typescript-eslint/naming-convention */
import MultiSelectInputFreeSolo from '@brands/Components/Inputs/MuiMultiSelectInputFreeSolo/MultiSelectInputFreeSolo';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import MuiToggle from '../../../../../Components/Inputs/MuiToggle/MuiToggle';
import { medicationOptions } from '../../../../MedicalHistory/MedicalHistory/utils/questions';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { sanitizeMedicalHistoryBooleans } from '../utils/medicalHistoryDefaults';
import styles from './styles.module.scss';

const ClinicalDataStepFour = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const [loading, setLoading] = useState(true);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [takingMedication, setTakingMedication] = useState<boolean | null>(null);
  const [medications, setMedications] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the patient actually edited this step, so we only confirm a
  // real save instead of showing a misleading toast on plain navigation.
  const userEditedRef = useRef(false);

  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: null,
    conditions_treated: null,
    taking_medication: null,
    medications: null,
    allergic_to_medications: null,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  } as MedicalHistory);

  const onSubmit = async (): Promise<void> => {
    if (bypassRequired && takingMedication === null) {
      sessionStorage.removeItem('medications');
      roleNavigate('/clinical-data/step-five');
      return;
    }

    try {
      setLoading(true);
      setHasUnsavedChanges(false);
      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatientId as number,
        ...medicalHistory,
        taking_medication: takingMedication,
        medications: takingMedication ? medications : null,
      };

      const sanitizedPayload = sanitizeMedicalHistoryBooleans(medicalHistoryPayload, bypassRequired);
      if (!historyObjectId) {
        await addClinicalData('medical_history', sanitizedPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), sanitizedPayload);
      }

      if (userEditedRef.current && !isIntakeProcess) {
        showSnackbar('Medical history saved successfully!', 'success');
      }
      sessionStorage.removeItem('medications');
      setLoading(false);
      roleNavigate('/clinical-data/step-five');
    } catch (error) {
      setHasUnsavedChanges(true);
      displayErrorDetails(error);
    }
  };
  const saveFormData = useCallback((): void => {
    if (hasUnsavedChanges && !loading) {
      const currentValues = {
        taking_medication: takingMedication,
        medications,
      };
      sessionStorage.setItem('medications', JSON.stringify(currentValues));
    }
  }, [hasUnsavedChanges, loading, takingMedication, medications]);

  useEffect(() => {
    let isActive = true;

    const hydrateClinicalData = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const savedDraft = sessionStorage.getItem('medications');
        const res = await listClinicalData(selectedPatientId);

        if (!isActive) return;

        if (res.medical_history) {
          setHistoryObjectId(res.medical_history.id || null);
          setMedicalHistory(res.medical_history);
        }

        const parsedData = savedDraft ? JSON.parse(savedDraft) : res.medical_history;
        setTakingMedication(parsedData?.taking_medication ?? null);
        setMedications(parsedData?.medications || null);
      } catch (error) {
        if (isActive) {
          displayErrorDetails(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
          setHasUnsavedChanges(true);
        }
      }
    };

    hydrateClinicalData();

    return () => {
      isActive = false;
    };
  }, [selectedPatientId]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={32} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Are you taking any medications?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
              <div className={styles.clinicalDataIntroSubTitle}>
                Within the last month, have you taken (or supposed to be taking) any medications? This includes
                prescription, over-the-counter, diet supplements, and natural or herbal vitamins.
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody} style={{ paddingBottom: '0px' }}>
              <div className={styles.formContent}>
                <MuiToggle
                  label="Are you taking any medications?"
                  hideLabel
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  selectedValue={takingMedication === null ? '' : takingMedication ? 'yes' : 'no'}
                  onChange={(val) => {
                    userEditedRef.current = true;
                    setTakingMedication(val === 'yes');
                    if (val === 'no') {
                      setMedications(null);
                    }
                  }}
                />
              </div>
            </div>
            {takingMedication && (
              <div className={styles.clinicalDataIntroBody}>
                <div className={styles.formContent}>
                  <label className={styles.clinicalDataIntroLabel} htmlFor="medications">
                    Search medications
                  </label>
                  <MultiSelectInputFreeSolo
                    label="Search medications"
                    errors={{}}
                    name="medications"
                    onChange={(selectedValues: string[]) => {
                      userEditedRef.current = true;
                      setMedications(selectedValues.join(', '));
                      setMedicalHistory({ ...medicalHistory, medications: selectedValues.join(', ') });
                    }}
                    hideLabel
                    styledTag
                    displayCheckbox={false}
                    value={medications ? medications.split(',').map((med) => med.trim()) : []}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileLanguage}`}
                    options={medicationOptions}
                  />
                </div>
              </div>
            )}
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={
                  loading ||
                  (!bypassRequired &&
                    (takingMedication === null ||
                      (takingMedication === true && (!medications || medications.trim() === ''))))
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (takingMedication !== null || bypassRequired) {
                    if (takingMedication && (!medications || medications.trim() === '')) {
                      showSnackbar('Please list your medications before proceeding.', 'error');
                      return;
                    }
                    onSubmit();
                  } else {
                    showSnackbar('Please fill in all required fields before proceeding.', 'error');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepFour;
